import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";

interface FormData {
  paymentMethod: string;
  bankName: string;
  bankNumber: string;
  accountHolderName: string;
}

interface FormErrors {
  paymentMethod?: string;
}

interface Props {
  formData: FormData;
  errors: FormErrors;
  onChange: (field: keyof FormData, value: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const paymentMethods = [
  { label: "Mobile Wallet", value: "mobile_wallet" },
  { label: "Bank Transfer", value: "bank_transfer" },
];

export default function CommissionInfoForm({
  formData,
  errors,
  onChange,
  onSubmit,
  isSubmitting,
}: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.primary[800],
      borderRadius: "16px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
    },
  };

  const labelSx = {
    fontWeight: 600,
    color: colors.primary[700],
    mb: 0.5,
    fontSize: "0.88rem",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box>
        <Typography variant="body2" sx={labelSx}>
          Preferred Payment Method *
        </Typography>
        <FormControl
          fullWidth
          size="small"
          error={!!errors.paymentMethod}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: colors.primary[800],
              borderRadius: "16px",
              "& fieldset": { border: "none" },
            },
          }}
        >
          <Select
            displayEmpty
            value={formData.paymentMethod}
            onChange={(e) => onChange("paymentMethod", e.target.value)}
            renderValue={(selected) => {
              if (!selected)
                return (
                  <Typography sx={{ color: "#A0938D" }}>
                    Select payment method
                  </Typography>
                );
              return (
                paymentMethods.find((p) => p.value === selected)?.label ||
                selected
              );
            }}
          >
            {paymentMethods.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
          {errors.paymentMethod && (
            <FormHelperText>{errors.paymentMethod}</FormHelperText>
          )}
        </FormControl>
      </Box>

      {formData.paymentMethod === "mobile_wallet" && (
        <>
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Mobile Wallet Name
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. EcoCash, OneMoney"
              size="small"
              value={formData.bankName}
              onChange={(e) => onChange("bankName", e.target.value)}
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Mobile Wallet Number
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. 0771234567"
              size="small"
              value={formData.bankNumber}
              onChange={(e) => onChange("bankNumber", e.target.value)}
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Account Holder Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Placeholder"
              size="small"
              value={formData.accountHolderName}
              onChange={(e) => onChange("accountHolderName", e.target.value)}
              sx={fieldSx}
            />
          </Box>
        </>
      )}

      {formData.paymentMethod === "bank_transfer" && (
        <>
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Bank Name
            </Typography>
            <TextField
              fullWidth
              placeholder="e.g. CBZ"
              size="small"
              value={formData.bankName}
              onChange={(e) => onChange("bankName", e.target.value)}
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Bank Account Number
            </Typography>
            <TextField
              fullWidth
              placeholder="Placeholder"
              size="small"
              value={formData.bankNumber}
              onChange={(e) => onChange("bankNumber", e.target.value)}
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Account Holder Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Placeholder"
              size="small"
              value={formData.accountHolderName}
              onChange={(e) => onChange("accountHolderName", e.target.value)}
              sx={fieldSx}
            />
          </Box>
        </>
      )}

      <Box sx={{ textAlign: "right" }}>
        <Button
          variant="contained"
          size="large"
          onClick={onSubmit}
          disabled={isSubmitting}
          sx={{
            backgroundColor: "#724E41",
            px: 5,
            py: 1.2,
            borderRadius: "24px",
            "&:hover": { backgroundColor: "#4E342E" },
            "&.Mui-disabled": { backgroundColor: "#A1887F", color: "#fff" },
          }}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
      </Box>
    </Box>
  );
}
